import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertToast } from '../../components';
import { api, call } from '../../api/client';
import { useTopicEvents } from '../../realtime/hooks';
import { t, tb, tf } from '../../i18n';
import { alertMessage } from '../../lib/names';
import { usePrefs } from '../prefs';
import type { Alert } from './data';

/**
 * Global alert toasts (SHL-02): a critical alert raises the pulsing toast on any page (with
 * "+N more in queue"), a warning raises the quieter toast that hides itself. When the tab is
 * hidden a critical alert also flashes the tab title and shows an OS notification (when the
 * browser allows it); the user's sound setting plays a short tone.
 */
export function LiveToasts({ enabled }: { enabled: boolean }) {
  const { sound } = usePrefs();
  const navigate = useNavigate();
  const [crit, setCrit] = useState<Alert[]>([]);
  const [warn, setWarn] = useState<Alert | null>(null);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  useTopicEvents(enabled ? 'alerts' : null, (e) => {
    const d = e.data as { kind?: string; alert?: Alert };
    if (d?.kind !== 'alert.created' || !d.alert || d.alert.status !== 'open') return;
    if (d.alert.late) return; // an old event draining from a buffer: it pages nobody, the feed and ALR-01 show it
    if (d.alert.severity === 'critical') {
      setCrit((q) => (q.some((x) => x.id === d.alert!.id) ? q : [...q, d.alert!]));
      signal(d.alert, soundRef.current);
    } else if (d.alert.severity === 'warning') setWarn(d.alert);
  });

  const next = useCallback(() => setCrit((q) => q.slice(1)), []);
  const snooze = useCallback(
    async (a: Alert) => {
      next();
      try {
        await call(api.POST('/alerts/{id}/snooze', { params: { path: { id: a.id } }, body: { minutes: 15 } }));
      } catch {
        /* the toast is gone either way; the alert stays in the feed */
      }
    },
    [next],
  );
  const go = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  const a = crit[0];
  if (a)
    return (
      <AlertToast
        key={a.id}
        sev="crit"
        inline={false}
        cycle={false}
        alert={toastItem(a)}
        eyebrow={t('app.toast.critical', 'CRITICAL · {type}', { type: typeLabel(a.type).toUpperCase() })}
        firedAt={a.fired_at}
        queue={crit.length - 1}
        onDismiss={next}
        onSnooze={() => void snooze(a)}
        onAction={(e: React.MouseEvent<HTMLAnchorElement>) => {
          next();
          go((e.currentTarget as HTMLAnchorElement).getAttribute('href') || '/alerts')(e);
        }}
      />
    );
  if (warn)
    return (
      <AlertToast
        key={warn.id}
        sev="warn"
        inline={false}
        cycle={false}
        alert={toastItem(warn)}
        eyebrow={t('app.toast.warning', 'WARNING · {type}', { type: typeLabel(warn.type).toUpperCase() })}
        firedAt={warn.fired_at}
        queue={0}
        onDismiss={() => setWarn(null)}
        onSnooze={() => setWarn(null)}
        onAction={(e: React.MouseEvent<HTMLAnchorElement>) => {
          setWarn(null);
          go((e.currentTarget as HTMLAnchorElement).getAttribute('href') || '/alerts')(e);
        }}
      />
    );
  return null;
}

export function typeLabel(type: string): string {
  return tb('alerts.type.' + type, type.replace(/_/g, ' '));
}

function toastItem(a: Alert) {
  const login = a.account?.login;
  return {
    account: login ? String(login) : a.symbol || '',
    cls: tf(a, 'title'),
    score: a.type === 'toxic_detection' && typeof a.observed === 'number' ? Math.round(a.observed) : null,
    detail: alertMessage(a),
    href: login
      ? '/account-overview?login=' + login + (a.account?.server_id ? '&server_id=' + encodeURIComponent(a.account.server_id) : '')
      : '/alert-detail?id=' + encodeURIComponent(a.id),
    caseHref: a.case_id ? '/case-detail?id=' + encodeURIComponent(a.case_id) : '/alert-detail?id=' + encodeURIComponent(a.id),
  };
}

// ---------- attention: sound, OS notification, tab title ----------

let audio: AudioContext | null = null;
function tone() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio = audio || new Ctx();
    const now = audio.currentTime;
    [0, 0.18].forEach((d, i) => {
      const o = audio!.createOscillator();
      const g = audio!.createGain();
      o.type = 'sine';
      o.frequency.value = i ? 660 : 880;
      g.gain.setValueAtTime(0.0001, now + d);
      g.gain.exponentialRampToValueAtTime(0.12, now + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + d + 0.16);
      o.connect(g).connect(audio!.destination);
      o.start(now + d);
      o.stop(now + d + 0.18);
    });
  } catch {
    /* audio blocked until the first user gesture */
  }
}

let flashTimer: ReturnType<typeof setInterval> | null = null;
let baseTitle = '';
function flashTitle(text: string) {
  if (flashTimer) return;
  baseTitle = document.title;
  let on = false;
  flashTimer = setInterval(() => {
    on = !on;
    document.title = on ? '● ' + text : baseTitle;
  }, 1000);
  const stop = () => {
    if (document.visibilityState !== 'visible') return;
    if (flashTimer) clearInterval(flashTimer);
    flashTimer = null;
    document.title = baseTitle;
    document.removeEventListener('visibilitychange', stop);
  };
  document.addEventListener('visibilitychange', stop);
}

function signal(a: Alert, sound: boolean) {
  if (sound) tone();
  if (document.visibilityState === 'hidden') {
    flashTitle(t('app.toast.title-flash', 'Critical alert'));
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification(tf(a, 'title'), { body: alertMessage(a), tag: a.id });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      }
    } catch {
      /* notifications unavailable */
    }
  }
}

/** Ask once for OS notifications, from a user gesture (profile or the toast). */
export function useNotificationPermission() {
  const [perm, setPerm] = useState(() => ('Notification' in window ? Notification.permission : 'denied'));
  useEffect(() => {
    if (!('Notification' in window)) return;
    setPerm(Notification.permission);
  }, []);
  const request = useCallback(async () => {
    if (!('Notification' in window)) return;
    setPerm(await Notification.requestPermission());
  }, []);
  return { perm, request };
}
